//Library imports
import React, { Fragment, useState, useEffect } from 'react';
import { useAsync } from 'react-async';
import { useHistory, useLocation } from 'react-router-dom';

//Services
import { getStudentOptions } from '../../api/services/studentService';
import { searchCaseloads } from '../../api/services/caseloadService';
import { getTherapistsByDisciplineId } from '../../api/services/therapistRolesService';
import { getStudentStatuses } from '../../api/services/studentStatusService';
import { getDistrictOptions } from '../../api/services/districtService';
import { getDisciplines } from '../../api/services/disciplineService';
import { getSchoolsByDistrict } from '../../api/services/schoolService';

//Models and data types
import caseloadDisplay from '../../api/models/caseloadDisplay';
import School from '../../api/models/school';
import { selectOption } from '../../types/selectOption';

//Components
import Layout from '../../components/layout';
import SubmitButton from '../../components/submitButton';
import DataTable from '../../components/dataTable';
import SelectMulti from 'react-select';
import { Notification } from '../../components/notifications';
import Discipline from '../../api/models/discipline';
import { getDisciplinesByTherapistId, getTherapistNamesAndIds } from '../../api/services/therapistService';
import moment from 'moment';
import Therapists from '../../api/models/therapists';
import AutoCompleteOption from '../../api/models/autoCompleteModel';
import FetchInput from '../../components/FetchInput';

//Get our view state
type ViewState = {
    message?: string;
    ids?: [];
};

/**
 * Methods to fetch search criteria with which to populate
 */
/*const fetchStudents = async (search: string) => {
    return await getStudentOptions(search);
};*/

const fetchTherapists = async () => await getTherapistNamesAndIds();
const fetchStudentStatuses = async () => getStudentStatuses();
const fetchDistricts = async () => getDistrictOptions();
const fetchDisciplines = async () => getDisciplines();
const fetchStudents = async (args: any[]) => getStudentOptions(args[0]);

/**
 * Primary Component
 */
const CaseloadsTransfer = () => {
    //Process our DataTable columns and data
    const columns = React.useMemo(
        () => [
            {
                Header: ' ',
                accessor: (s: any) => {
                    return (
                        <input
                            id={`caseload_${s.caseloadId}`}
                            className="datatable-checkbox"
                            type='checkbox'
                            value={s.caseloadId}
                            onClick={(e: any) => {
                                e.stopPropagation();
                                setSelectedCaseloadIds(current => {
                                    var caseloadId = Number(e.target.value);
                                    const temp = current;
                                    if (!e.target?.checked) {
                                        return temp.filter(x => x !== caseloadId)
                                    }

                                    return [...temp, caseloadId]
                                })
                            }}
                        />
                    )
                },
                width: 30,
                disableSortBy: true
            },
            {
                Header: 'Student',
                accessor: (s: any) => `${s.studentLastName}, ${s.studentFirstName}`,
            },
            {
                Header: 'Discipline',
                accessor: (s: any) => s.disciplineName
            },
            {
                Header: 'District',
                accessor: (s: any) => s.districtName,
                width: 250,
            },
            {
                Header: 'School',
                accessor: (s: any) => s.schoolName
            },
            {
                Header: 'Status',
                accessor: (s: any) => s.studentStatus
            },
            {
                Header: 'Grade',
                accessor: (s: any) => s.gradeName,
            },
            {
                Header: 'IEP',
                accessor: (s: any) => s.iepStartDate === null || s.iepEndDate === null ? '' : `${moment(s.iepStartDate).format('l')} - ${moment(s.iepEndDate).format('l')}`,
            },
            {
                Header: 'Therapist Type',
                accessor: (s: any) => s.therapyType,
            },
            {
                Header: 'Primary Therapist',
                accessor: (s: any) => `${s.therapistLastName}, ${s.therapistFirstName}`,
            },
            {
                accessor: 'studentId',
                className: 'hidden',
            },
            {
                accessor: 'caseloadId',
                className: 'hidden',
            },
        ],
        //Disabling next line for eslint only because we need an empty dependency array so the effec tis only called once, on load
        // eslint-disable-next-line
        []
    );

    //App hooks
    const { state } = useLocation<ViewState>();
    const history = useHistory();

    //Select element options
    const [studentOptions, setStudentOptions] = useState<AutoCompleteOption[]>([{ id: -1, name: "enter text to search" }]);
    const [therapistOptions, setTherapistOptions] = useState<selectOption[]>([{ value: -1, label: "enter text to search" }]);
    const [schoolOptions, setSchoolOptions] = useState<selectOption[]>([{ value: -1, label: '' }]);
    const [statusOptions, setStatusOptions] = useState<selectOption[]>([{ value: -1, label: '' }]);
    const [districtOptions, setDistrictOptions] = useState<selectOption[]>([{ value: -1, label: '' }]);
    const [disciplineOptions, setDisciplineOptions] = useState<selectOption[]>([{ value: -1, label: '' }]);


    //Data hooks
    const [therapist, setTherapist] = useState<number>(-1);
    const [school, setSchool] = useState<number>(-1);
    const [status, setStatus] = useState<number>(-1);
    const [district, setDistrict] = useState<number>(-1);
    const [disciplineId, setDisciplineId] = useState<number>(-1);
    const [disciplineGroupId, setDisciplineGroupId] = useState<number | null>(null);


    const [name, setName] = useState('');
    const [caseloads, setCaseloads] = useState<caseloadDisplay[]>([]);
    //const [selectedCaseloads, setSelectedCaseloads] = useState<number[]>([]);
    const [isSelect, setIsSelect] = useState<boolean>(true);

    const [therapistName, setTherapistName] = useState<string>('');
    const [districtName, setDistrictName] = useState<string>('');
    const [schoolName, setSchoolName] = useState<string>('');
    const [disciplineName, setDisciplineName] = useState<string>('');
    const [selectedCaseloadIds, setSelectedCaseloadIds] = useState<number[]>([]);

    /**
     * Options lists (for our select elements)
     */
    const getTherapistsAutoCompleteAsync = useAsync({
        promiseFn: fetchTherapists,
        onResolve: (u) => setTherapistOptions([...u.data])
    });

    const studentStatusesData = useAsync({
        promiseFn: fetchStudentStatuses,
        onResolve: (u) => {
            let options: selectOption[] = statusOptions;

            //set options           
            (u.data.studentStatuses.map((g) => (
                options.push({ value: g.studentStatusId ?? -1, label: g.name })
            )));
            options.sort((a, b) => a.label.localeCompare(b.label));
            setStatusOptions(options);
        },
    });

    const districtsData = useAsync({
        promiseFn: fetchDistricts,
        onResolve: (u) => setDistrictOptions(u.data)
    });

    const disciplinesData = useAsync({
        promiseFn: fetchDisciplines,
        onResolve: (u) => {
            let options: selectOption[] = disciplineOptions;

            //set options           
            (u.data.disciplines.map((g) => (
                options.push({ value: g.disciplineId ?? -1, label: g.name })
            )));
            options.sort((a, b) => a.label.localeCompare(b.label));
            setDisciplineOptions(options);
        },
    });

    /**
     * Search Handling Logic
     */
    const handleSearch = async () => {
        if (Number.isNaN(therapist)) setTherapist(-1);
        const therapistId = (Number.isNaN(therapist) ? -1 : therapist);

        try {
            const response = await searchCaseloads(name, therapistId, status, district, school, -1, disciplineId, -1, -1, therapistName, disciplineName, districtName, schoolName);

            //Loop through results to process
            let data: caseloadDisplay[] = response.data.caseloads;
            let ids: number[] = [];
            data.map((caseload) => {
                ids.push(caseload.caseloadId);
                return caseload.caseloadId;
            });


            setCaseloads(data);
        } catch (error: any) {
            console.log(error);
        }

        return new Promise<void>((resolve) => resolve());
    };
    const handleSearchAsync = useAsync({ deferFn: handleSearch });

    /**
     * Whenever district changes, update the list of schools
     */
    useEffect(() => {
        if (district > 0) {
            updateSchools(district);
        }
    }, [district, districtName]);

    /**
     * Whenever therapist changes, update the list of disciplines
     */
    useEffect(() => {
        updateDisciplines(therapist);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [therapist]);

    /**
     * This effect updates the therapists every time the disciplineId is changed
     */
    useEffect(() => {
        if (disciplineId) {
            const updateTherapists = async () => {
                const response = await getTherapistsByDisciplineId(disciplineId);
                const therapists: Therapists[] = response.data.therapists;

                if (therapists.length > 0) {
                    let options: selectOption[] = [];
                    //set options           
                    therapists.map((t: Therapists) => {
                        if (t?.disciplineId === disciplineId) {
                            //options.push({ id: t.userId ?? -1, name: t.user?.lastName + ", " + t.user?.firstName });
                            options.push({ value: t.userId ?? -1, label: `${t?.user?.lastName}, ${t?.user?.firstName}`, altLabel: `${t.user?.firstName} ${t.user?.lastName}` })
                        }
                        return true;
                    });

                    setTherapistOptions([...options]);
                }
            }

            //Calling the above async so we only set the caseloads after they are all returned
            updateTherapists();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [disciplineId]);

    /**
     * Functions to update the select boxes
     */
    const updateSchools = async (district: number) => {
        const response = await getSchoolsByDistrict(district);
        const schoolsData: School[] = response.data.schools;
        const schools: selectOption[] = [{ value: -1, label: '' }];
        schoolsData.map((school) => {
            schools.push({ value: school.schoolId ?? -1, label: school.name });
            return true;
        });
        schools.sort((a, b) => a.label.localeCompare(b.label));
        setSchoolOptions(schools);
    }

    const updateDisciplines = async (discipline: number) => {
        const response = (discipline > 0) ? await getDisciplinesByTherapistId(therapist) : await getDisciplines();
        const disciplinesData: Discipline[] = response.data.disciplines;
        const disciplines: selectOption[] = [{ value: -1, label: '' }];
        disciplinesData.map((discipline) => {
            disciplines.push({ value: discipline.disciplineId ?? -1, label: discipline.name });
            return true;
        });
        setDisciplineOptions(disciplines);
    }

    /**
     * Returns an array of selected caseloads from the DOM
     * This has been found to be more consistent
     * @returns number[]
     */
    const getSelectedCaseloads = (): number[] => {
        let selectedCaseloads: number[] = [];

        const checkboxes = document.querySelectorAll('.datatable-checkbox:checked');

        checkboxes.forEach((checkbox: any) => {
            selectedCaseloads.push(checkbox.value);
        });


        return selectedCaseloads;
    }

    // const validateSelectedCaseloads = (): boolean => {
    //     let caseloadIds = selectedCaseloadIds;
    //     let disciplineGrp = null;
    //     //loop through selected items and build out selected students
    //     for (var i = 0; i < caseloadIds.length; i++) {

    //         //make sure we don't have mixed desciplines
    //         let caseload = caseloads.find(c => c.caseloadId === Number(caseloadIds[i]));
    //         if (disciplineId !== caseload?.disciplineId && disciplineId !== -1) {
    //             alert("Selected caseloads must be under the same discipline. Please make sure all selected caseloads have the same discipline");
    //             return false;
    //         }
    //         setDisciplineId(caseload?.disciplineId!);

    //         disciplineGrp = caseload?.disciplineGroupId;
    //     }
    //     if (disciplineGrp != null) {
    //         setDisciplineGroupId(disciplineGrp)
    //         return true;
    //     }

    //     return false;
    // }

    const validateSelectedCaseloads = (): boolean => {
        const caseloadIds = selectedCaseloadIds;
        let firstCaseloadDisciplineId: number | null = null;
        let disciplineGrp: number | null = null;

        for (let i = 0; i < caseloadIds.length; i++) {
            const caseload = caseloads.find(c => c.caseloadId === Number(caseloadIds[i]));

            if (!caseload) continue;

            if (firstCaseloadDisciplineId === null) {
                firstCaseloadDisciplineId = caseload.disciplineId;
                disciplineGrp = caseload.disciplineGroupId;
            } else if (caseload.disciplineId !== firstCaseloadDisciplineId) {
                alert("Selected caseloads must be under the same discipline. Please make sure all selected caseloads have the same discipline.");
                return false;
            }
        }

        if (firstCaseloadDisciplineId !== null && disciplineGrp !== null) {
            setDisciplineId(firstCaseloadDisciplineId);
            setDisciplineGroupId(disciplineGrp);
            return true;
        }

        return false;
    };


    useEffect(() => {
        if (disciplineGroupId != null) {
            // Only trigger navigation once disciplineGroupId is properly set
            history.push(`/caseloadTransfer/transfer`, {
                ids: getSelectedCaseloads(),
                disciplineId: disciplineId,
                disciplineGroupId: disciplineGroupId
            });
        }
    }, [disciplineGroupId]);  // Watch for disciplineGroupId change


    const resetSelection = () => {
        setSelectedCaseloadIds([]);
        const checkboxes = document.querySelectorAll('.datatable-checkbox');
        checkboxes.forEach(box => {
            (box as HTMLInputElement).checked = false
        });
    }

    /**
     * When the page size changes on the table and the select all button
     * is enabled, then check all the new checkboxes as well
     * @param pageSize number
     */
    const onChangePageSize = (pageSize: number) => {
        if (!isSelect) {
            document.querySelectorAll('.datatable-checkbox')
                .forEach((checkbox: any) => !checkbox.checked ? checkbox.click() : true);
        }
    }

    const handleTransferClick = () => {
        //make sure the caseloads selected are valid
        validateSelectedCaseloads();
    }



    return (
        <Layout isLoading={
            handleSearchAsync.isPending ||
            //studentsData.isPending ||
            getTherapistsAutoCompleteAsync.isPending ||
            districtsData.isPending ||
            studentStatusesData.isPending ||
            disciplinesData.isPending ||
            handleSearchAsync.isPending
        }>
            <Fragment>
                <Fragment>
                    {state?.message && <Notification>{state?.message}</Notification>}

                    {/* Select Boxes */}
                    <div className='z-0 px-4 py-5 bg-white sm:p-6'>
                        <div className='z-0 grid grid-cols-6 gap-6'>
                            <div className='col-span-6 sm:col-span-6 lg:col-span-2'>
                                <FetchInput
                                    value={name}
                                    rounded='sm'
                                    label={`Student Name`}
                                    fetchProperty={`fullNameLtr`}
                                    altFetchProperty={`fullName`}
                                    valueProperty={`fullNameLtr`}
                                    fetchFunction={fetchStudents}
                                    onClear={() => setName('')}
                                    onChange={(value: string) => setName(value)}
                                    icon={'search'}
                                />
                            </div>

                            <div className='col-span-6 sm:col-span-3 lg:col-span-2'>
                                <FetchInput
                                    value={therapistName}
                                    rounded='sm'
                                    placeholder=''
                                    label={`Therapist`}
                                    key={"therapist_ac"}
                                    fetchProperty={`altName`}
                                    altFetchProperty={`name`}
                                    valueProperty={`altName`}
                                    returnRawValue
                                    fetchData={therapistOptions}
                                    onClear={() => setTherapistName('')}
                                    onChange={(value: string) => setTherapistName(value)}
                                    icon={'search'}
                                />
                            </div>

                            <div className='col-span-6 sm:col-span-3 lg:col-span-2'>
                                <label className='primary'>Status</label>
                                <SelectMulti
                                    className='block primary'
                                    isMulti={false}
                                    options={statusOptions}
                                    isClearable={true}
                                    onChange={(e: any | null) => {
                                        //Null exception handling
                                        e = (e !== null) ? e : { label: '', value: -1 };
                                        setStatus(Number(e.value))
                                    }}
                                />
                            </div>
                            <div className='col-span-6 sm:col-span-3 lg:col-span-2'>
                                <FetchInput
                                    value={districtOptions.find((x) => x.value === district)?.label ?? ''}
                                    rounded='sm'
                                    placeholder=''
                                    label={`Districts`}
                                    fetchProperty={`label`}
                                    valueProperty={`value`}
                                    fetchData={districtOptions}
                                    onChange={(value: string) => {
                                        setDistrictName(value);
                                        //setDistrict(value);
                                    }}
                                    onClear={() => {
                                        setDistrictName('');
                                        //setDistrict(-1);
                                    }}
                                    icon={'search'}
                                />
                            </div>
                            <div className='col-span-6 sm:col-span-3 lg:col-span-2'>
                                <FetchInput
                                    value={schoolOptions.find((x) => x.value === school)?.label ?? ''}
                                    rounded='sm'
                                    placeholder=''
                                    label={`School`}
                                    fetchProperty={`label`}
                                    valueProperty={`value`}
                                    returnRawValue={true}
                                    fetchData={schoolOptions}
                                    onChange={(value: string | number) => setSchoolName(value.toString())}
                                    onClear={() => setSchoolName('')}
                                    icon={'search'}
                                />
                            </div>
                            <div className='col-span-6 sm:col-span-3 lg:col-span-2'>
                                <FetchInput
                                    value={disciplineOptions.find((x) => x.value === disciplineId)?.label ?? ''}
                                    rounded='sm'
                                    placeholder=''
                                    label={`Discipline`}
                                    fetchProperty={`label`}
                                    valueProperty={`value`}
                                    returnRawValue={true}
                                    fetchData={disciplineOptions}
                                    onChange={(value: string | number) => setDisciplineName(value.toString())}
                                    onClear={() => setDisciplineName('')}
                                    icon={'search'}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Button Row */}
                    <div className='flex px-4 py-3 text-right bg-gray-50 sm:px-6'>
                        <SubmitButton
                            text='Search'
                            className='mr-5 bg-ptsblue-normal hover:bg-ptsblue-dark'
                            onClick={() => {
                                resetSelection();
                                handleSearchAsync.run();
                            }}
                        />
                        <SubmitButton
                            text={isSelect ? 'Select All' : 'Unselect All'}
                            className='mr-5 bg-ptsblue-normal hover:bg-ptsblue-dark'
                            disabled={caseloads.length === 0}
                            onClick={() => {
                                const checkboxes = document.querySelectorAll('.datatable-checkbox');
                                checkboxes.forEach((checkbox: any) => {
                                    if (isSelect) {
                                        if (!checkbox.checked) checkbox.click();
                                        setSelectedCaseloadIds(caseloads.flatMap(x => x.caseloadId));
                                    } else {
                                        if (checkbox.checked) checkbox.click();
                                        setSelectedCaseloadIds([]);
                                    }
                                });

                                //Toggle the select all status
                                setIsSelect(!isSelect);
                            }}
                        />
                        <SubmitButton
                            text='Transfer'
                            className='mr-5 primary'
                            onClick={() => { handleTransferClick() }}
                            disabled={selectedCaseloadIds.length < 1}
                        />
                    </div>

                    {/* Caseloads Display Table */}
                    {caseloads.length > 0 && (
                        <div className='overflow-hidden border-b border-gray-200 rounded-lg shadow'>
                            <DataTable
                                columns={columns}
                                data={caseloads}
                                showPagination={true}
                                getRowProps={(row: any) => ({
                                    onClick: () => {
                                        document.getElementById(`caseload_${row.values.caseloadId}`)?.click();
                                    }
                                })}
                                onChangePageSize={onChangePageSize}
                            />
                        </div>
                    )}

                    {/* Students Not Found Message */}
                    {caseloads.length === 0 && (
                        <div>
                            <label>No students found.</label>
                            <div className='px-4 py-3 bg-gray-100 sm:px-6 sm:flex sm:flex-row-reverse'>

                            </div>
                        </div>
                    )}
                </Fragment>
            </Fragment>
        </Layout>

    );
};

export default CaseloadsTransfer;
